import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { ITimeZoneDTO, ITimeZonesDAO } from '../../../api/timezones.dao';
import { DeepReadonly } from '../../../infrastructure/type-extensions';
import { timezonesCodeLabelMap } from '../../../infrastructure/timezones-code-label-map';
import { formatTimezoneOffset, getTimezoneOffsetInMinutes } from '../../../infrastructure/utils/date-fns-overrides.utils';

class SelectTimezonesVM {
    @mobx.observable
    private timezoneList: ITimeZoneDTO[];

    @mobx.observable
    private notSupportedTimezoneOption: ITimeZoneDTO;

    private isPickingTimezones: boolean;

    constructor(@IoC.Inject private readonly timezonesDAO: ITimeZonesDAO) {
        this.notSupportedTimezoneOption = null;
    }

    public getTimezonesList() {
        const listToReturn = this.timezoneList ? this.timezoneList.slice() : null;

        // pick all timezones on first access
        if (listToReturn == null && !this.isPickingTimezones) {
            this.pickTimezones();
        }
        if (listToReturn !== null && this.notSupportedTimezoneOption !== null && !this.isPickingTimezones) {
            listToReturn.unshift(this.notSupportedTimezoneOption);
        }

        return listToReturn as DeepReadonly<ITimeZoneDTO[]>;
    }

    @mobx.action
    public setSelectedTimezone = (timezone: string) => {
        if (timezone !== null && !timezonesCodeLabelMap[timezone]) {
            const offsetInMinutes = getTimezoneOffsetInMinutes(timezone);
            const stringifiedOffset = formatTimezoneOffset(offsetInMinutes);
            this.notSupportedTimezoneOption = {
                code: timezone,
                offset: stringifiedOffset
            };
        } else {
            this.notSupportedTimezoneOption = null;
        }
    };

    @mobx.action
    public async pickTimezones() {
        this.isPickingTimezones = true;
        try {
            const timezonesDTO = await this.timezonesDAO.getTimeZones();
            this.processTimezonesResult(timezonesDTO);
        } catch (e) {
            this.isPickingTimezones = false;
        }
    }

    @mobx.action
    private processTimezonesResult(timezonesList: ITimeZoneDTO[]) {
        this.timezoneList = timezonesList.map(timezone => {
            return {
                code: timezone.code,
                offsetInMinutes: getTimezoneOffsetInMinutes(timezone.code)
            };
        }).sort((timezone1, timezone2) => {
            const offsetDiff = timezone1.offsetInMinutes - timezone2.offsetInMinutes;
            if (offsetDiff !== 0) {
                return offsetDiff;
            } else {
                return timezone1.code.localeCompare(timezone2.code);
            }
        }).map(timezone => {
            return {
                code: timezone.code,
                offset: formatTimezoneOffset(timezone.offsetInMinutes),
            };
        });
        this.isPickingTimezones = false;
    }
}

export { SelectTimezonesVM };
